import { useCallback, useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Wallet, Ticket, Scale, Plus, Trash2, Pencil, Check, X, Copy, FileSpreadsheet, Settings2, ArrowRightLeft, Info, BookOpen, Columns3, ListChecks, PieChart } from 'lucide-react';
import {
  getFestival, getComparativo, setFestivalConfig, addRenglon, updateRenglon, deleteRenglon, copyPresupuesto,
  addMovimiento, updateMovimiento, deleteMovimiento, addEventExpense, deleteEventExpense, pasarUtilidad, quitarUtilidad, urlFestivalXlsx,
  FestResumen, FestComparativo, FestRenglon, FestMov, FestKind, money,
} from '../api';
import { getCatalog, FinConcept } from '../../finances/api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { useToast } from '../../../context/ToastContext';
import { todayLocal as todayISO } from '../../../utils/dates';

// «Finanzas del festival» (Mabel 26-oct-2026): presupuesto por renglón, libreta del evento, resumen con boletos y utilidad,
// comparativo entre festivales y el puente a Finanzas. Mismo lenguaje visual que las fichas (cabeceras h3 con línea).
const h3Txt = 'flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content';
const h3Cls = `mb-2 border-b border-line pb-1.5 ${h3Txt}`;
const h3Row = 'mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-line pb-1.5';
const inputCls = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';
const btnPri = 'inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent hover:opacity-90 disabled:cursor-not-allowed disabled:border disabled:border-line disabled:bg-surface-2 disabled:text-content-3 disabled:hover:opacity-100';
const btnSec = 'inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 hover:bg-surface-2';
const signo = (n: number) => (n < 0 ? `−${money(Math.abs(n))}` : money(n));
const fmtCorta = (f: string | null) => {
  const m = f?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
};
const METODO: Record<string, string> = { cash: 'Efectivo', wire: 'Transferencia', creditcard: 'Tarjeta' };
type Sub = 'resumen' | 'presupuesto' | 'movimientos' | 'comparativo';

function Kpi({ icon: Icon, label, value, sub, tone }: { icon: typeof Wallet; label: string; value: string; sub?: string; tone?: 'pos' | 'neg' }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><Icon size={13} /> {label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${tone === 'pos' ? 'text-emerald-600' : tone === 'neg' ? 'text-red-500' : 'text-content'}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-content-3">{sub}</div>}
    </div>
  );
}

export default function FestivalFinanzas({ eventId, isAdmin, initialSub }: { eventId: number; isAdmin: boolean; initialSub?: Sub }) {
  const toast = useToast();
  const [r, setR] = useState<FestResumen | null>(null);
  const [comp, setComp] = useState<FestComparativo | null>(null);
  const [sub, setSub] = useState<Sub>(initialSub || 'resumen');
  const [config, setConfig] = useState(false);

  const load = useCallback(() => {
    getFestival(eventId).then(setR).catch(() => setR((p) => p));
    getComparativo().then(setComp).catch(() => setComp((p) => p ?? { columnas: [], gastos: [] }));
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  if (!r) return <Skeleton className="h-64 w-full rounded-2xl" />;
  const ev = r.evento;
  const ref = ev.is_reference;
  const tabs: [Sub, string, typeof Wallet][] = [
    ['resumen', 'Resumen', PieChart], ['presupuesto', ref ? 'Cifras' : 'Presupuesto', ListChecks],
    ...(ref ? [] : [['movimientos', 'Movimientos', BookOpen] as [Sub, string, typeof Wallet]]), ['comparativo', 'Comparativo', Columns3],
  ];

  return (
    <div className="space-y-4">
      {/* Cómo entra este evento a Finanzas */}
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-line bg-surface-2/60 px-4 py-3">
        <div className="flex min-w-0 items-start gap-2 text-sm text-content-2">
          <Info size={16} className="mt-0.5 shrink-0 text-brand-blue" />
          <div>
            {ref ? <><strong className="text-content">Antecedente.</strong> Cifras capturadas de tu hoja, solo para comparar y proyectar: no mueven ningún reporte.</>
              : ev.fin_mode === 'utilidad' ? <><strong className="text-content">Libreta propia del festival.</strong> Lo que captures aquí no entra a Finanzas; al final pasas la utilidad a Ingresos como «{r.finanzas && 'concepto' in r.finanzas && r.finanzas.concepto ? r.finanzas.concepto.name : 'sin concepto'}».</>
              : <><strong className="text-content">En Finanzas completo.</strong> Los boletos cobrados en Caja (tipo «Evento», con el nombre del evento) y sus gastos ya cuentan en Finanzas; aquí se ven juntos y contra el presupuesto.</>}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <a href={urlFestivalXlsx(eventId)} className={btnSec}><FileSpreadsheet size={15} /> Excel</a>
          {isAdmin && <button onClick={() => setConfig((v) => !v)} className={btnSec}><Settings2 size={15} /> Ajustes</button>}
        </div>
      </div>
      {config && isAdmin && <Ajustes r={r} eventId={eventId} onDone={() => { setConfig(false); load(); }} />}

      <div className="grid grid-flow-col auto-cols-fr border-b border-line sm:flex sm:gap-1">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setSub(k)} data-sub={k}
            className={`inline-flex min-w-0 shrink-0 items-center justify-center gap-1.5 border-b-2 px-1 py-2 text-xs font-medium transition sm:px-3 sm:text-sm ${sub === k ? 'border-accent text-content' : 'border-transparent text-content-3 hover:text-content-2'}`}>
            <Icon size={14} className="hidden sm:block" /> {label}
          </button>
        ))}
      </div>

      {sub === 'resumen' && <Resumen r={r} eventId={eventId} isAdmin={isAdmin} onChange={load} />}
      {sub === 'presupuesto' && <Presupuesto r={r} eventId={eventId} isAdmin={isAdmin} comp={comp} onChange={load} toast={toast} />}
      {sub === 'movimientos' && !ref && <Movimientos r={r} eventId={eventId} isAdmin={isAdmin} onChange={load} toast={toast} />}
      {sub === 'comparativo' && <Comparativo comp={comp} actual={eventId} />}
    </div>
  );
}

// ── Resumen ───────────────────────────────────────────────────────────────────
function Resumen({ r, eventId, isAdmin, onChange }: { r: FestResumen; eventId: number; isAdmin: boolean; onChange: () => void }) {
  const toast = useToast();
  const p = r.presupuesto, re = r.real, ref = r.evento.is_reference;
  const [busy, setBusy] = useState(false);
  const ing = p.renglones.filter((l) => l.kind === 'ingreso'), gas = p.renglones.filter((l) => l.kind === 'gasto');
  const f = r.finanzas && r.finanzas.modo === 'utilidad' ? r.finanzas : null;

  async function pasar() {
    if (!f || !confirm(`Se registrará un ingreso de ${money(f.aPasar)} en Finanzas («${f.concepto?.name}»). ¿Continuar?`)) return;
    setBusy(true);
    try { const x = await pasarUtilidad(eventId); toast.success(`Utilidad pasada a Ingresos: ${money(x.amount)}`); onChange(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo'); } finally { setBusy(false); }
  }
  async function quitar() {
    if (!confirm('¿Quitar de Finanzas el ingreso de la utilidad?')) return;
    setBusy(true);
    try { await quitarUtilidad(eventId); toast.success('Ingreso quitado'); onChange(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo'); } finally { setBusy(false); }
  }

  const fila = (l: FestRenglon) => (
    <tr key={l.id} className="border-t border-line/60">
      <td className="px-3 py-2 text-content">{l.concept}{l.qty != null && l.unit_price != null && <span className="ml-1 text-xs text-content-3">({l.qty} × {money(l.unit_price)})</span>}</td>
      <td className="px-3 py-2 text-right tabular-nums text-content">{money(l.amount)}</td>
      {!ref && <td className="px-3 py-2 text-right tabular-nums text-content">{l.real ? money(l.real) : <span className="text-content-3">—</span>}</td>}
      {!ref && <td className={`px-3 py-2 text-right tabular-nums ${!l.real ? 'text-content-3' : (l.kind === 'ingreso' ? (l.diferencia || 0) >= 0 : (l.diferencia || 0) <= 0) ? 'text-emerald-600' : 'text-red-500'}`}>{l.real ? signo(l.diferencia || 0) : '—'}</td>}
    </tr>
  );
  const subtotal = (label: string, pres: number, real: number) => (
    <tr className="border-t border-line bg-surface-2/60 font-semibold">
      <td className="px-3 py-2 text-content">{label}</td>
      <td className="px-3 py-2 text-right tabular-nums text-content">{money(pres)}</td>
      {!ref && <td className="px-3 py-2 text-right tabular-nums text-content">{money(real)}</td>}
      {!ref && <td />}
    </tr>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi icon={TrendingUp} label="Ingresos" value={money(ref ? p.ingresos : re.ingresos)} sub={ref ? 'de tu hoja' : `presupuesto ${money(p.ingresos)}`} />
        <Kpi icon={TrendingDown} label="Gastos" value={money(ref ? p.gastos : re.gastos)} sub={ref ? 'de tu hoja' : `presupuesto ${money(p.gastos)}`} />
        <Kpi icon={Wallet} label="Utilidad" value={signo(ref ? p.utilidad : re.utilidad)} tone={(ref ? p.utilidad : re.utilidad) >= 0 ? 'pos' : 'neg'}
          sub={ref ? (p.ingresos ? `margen ${Math.round((100 * p.utilidad) / p.ingresos)}%` : undefined) : `presupuesto ${signo(p.utilidad)}`} />
        <Kpi icon={Ticket} label="Boletos" value={ref ? String(p.boletos.meta ?? '—') : String(re.boletos.vendidos)}
          sub={ref ? (p.boletos.precio ? `a ${money(p.boletos.precio)}` : undefined) : `meta ${p.boletos.meta ?? '—'}${p.boletos.precio ? ` · a ${money(p.boletos.precio)}` : ''}`} />
        <Kpi icon={Scale} label="Para salir a mano" value={p.equilibrio != null ? `${p.equilibrio} boletos` : '—'}
          sub={p.equilibrio != null && !ref ? (re.boletos.vendidos >= p.equilibrio ? '¡ya se cubrieron los gastos!' : `faltan ${p.equilibrio - re.boletos.vendidos}`) : 'con los gastos presupuestados'} />
      </div>

      {f && (
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <h3 className={h3Cls}><ArrowRightLeft size={13} className="text-accent-ink" /> Utilidad a Finanzas</h3>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-content-2">
              {f.pagado
                ? <>Ya está en Finanzas: <strong className="text-content">{money(f.pagado.amount)}</strong> el {fmtCorta(f.pagado.fecha)} como «{f.concepto?.name}».</>
                : <>Al cerrar el festival pasa a Ingresos como «{f.concepto?.name || 'sin concepto'}»: <strong className="text-content">{signo(f.aPasar)}</strong>
                  {f.enCaja > 0 && <> (utilidad {signo(re.utilidad)} menos {money(f.enCaja)} de boletos que ya se cobraron en Caja y ya están en Finanzas)</>}.
                  {f.motivo && <span className="block text-xs text-content-3">{f.motivo}. También la puedes capturar a mano en Finanzas › Registrar ingreso con ese concepto.</span>}</>}
            </div>
            {isAdmin && (f.pagado
              ? <button onClick={quitar} disabled={busy} className={btnSec}><X size={15} /> Quitar de Finanzas</button>
              : <button onClick={pasar} disabled={busy || !f.puedePasar} className={btnPri} title={f.motivo || ''}><ArrowRightLeft size={15} /> Pasar la utilidad a Ingresos</button>)}
          </div>
        </div>
      )}

      <div>
        <h3 className={h3Cls}><ListChecks size={13} className="text-accent-ink" /> {ref ? 'Cifras del festival' : 'Presupuesto contra real'}</h3>
        {p.renglones.length === 0 ? (
          <EmptyState icon={ListChecks} title="Sin presupuesto" subtitle="Captúralo en la pestaña Presupuesto (o cópialo de otro festival)." compact />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-line bg-surface">
            <table className="w-full min-w-[520px] text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-content-3">
                <th className="px-3 py-2">Concepto</th><th className="px-3 py-2 text-right">{ref ? 'Monto' : 'Presupuesto'}</th>
                {!ref && <th className="px-3 py-2 text-right">Real</th>}{!ref && <th className="px-3 py-2 text-right">Diferencia</th>}
              </tr></thead>
              <tbody>
                <tr className="border-t border-line"><td colSpan={ref ? 2 : 4} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-600">Ingresos</td></tr>
                {ing.map(fila)}
                {subtotal('Total de ingresos', p.ingresos, re.ingresos)}
                <tr className="border-t border-line"><td colSpan={ref ? 2 : 4} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-red-500">Gastos</td></tr>
                {gas.map(fila)}
                {subtotal('Total de gastos', p.gastos, re.gastos)}
                <tr className="border-t-2 border-line font-bold">
                  <td className="px-3 py-2.5 text-content">Resultado (utilidad)</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums ${p.utilidad >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{signo(p.utilidad)}</td>
                  {!ref && <td className={`px-3 py-2.5 text-right tabular-nums ${re.utilidad >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{signo(re.utilidad)}</td>}
                  {!ref && <td />}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {!ref && re.sinRenglon.length > 0 && (
          <p className="mt-2 text-xs text-content-3">Hay {re.sinRenglon.length} movimiento(s) sin renglón del presupuesto ({money(re.sinRenglon.reduce((s, m) => s + (m.kind === 'gasto' ? -m.amount : m.amount), 0))} neto): cuentan en los totales; en Movimientos los puedes ligar a un renglón.</p>
        )}
      </div>
    </div>
  );
}

// ── Presupuesto (editable) ────────────────────────────────────────────────────
function Presupuesto({ r, eventId, isAdmin, comp, onChange, toast }: { r: FestResumen; eventId: number; isAdmin: boolean; comp: FestComparativo | null; onChange: () => void; toast: ReturnType<typeof useToast> }) {
  const [edit, setEdit] = useState<number | null>(null);
  const [nuevo, setNuevo] = useState<FestKind | null>(null);
  const [desde, setDesde] = useState('');
  const otros = (comp?.columnas || []).filter((c) => c.event_id !== eventId);
  const grupo = (kind: FestKind) => r.presupuesto.renglones.filter((l) => l.kind === kind);

  async function guardar(l: Partial<FestRenglon> & { kind: FestKind }, id?: number) {
    try {
      const body = { kind: l.kind, tipo: l.tipo || 'otro', concept: l.concept || '', qty: l.qty ?? null, unit_price: l.unit_price ?? null, amount: l.amount ?? null, note: l.note ?? null };
      if (id) await updateRenglon(eventId, id, body); else await addRenglon(eventId, body);
      setEdit(null); setNuevo(null); onChange();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
  }
  async function borrar(l: FestRenglon) {
    if (!confirm(`¿Borrar «${l.concept}»?`)) return;
    try { await deleteRenglon(eventId, l.id); onChange(); } catch (e: any) { toast.error(e?.message || 'No se pudo borrar'); }
  }
  async function copiar() {
    if (!desde) return;
    const c = otros.find((x) => String(x.event_id) === desde);
    if (!confirm(`¿Copiar los renglones de «${c?.nombre}»? Se suman a los que ya tengas.`)) return;
    try { const x = await copyPresupuesto(eventId, Number(desde)); toast.success(`${x.copiados} renglones copiados`); setDesde(''); onChange(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo copiar'); }
  }

  const tabla = (kind: FestKind) => {
    const ls = grupo(kind);
    const total = ls.reduce((s, l) => s + l.amount, 0);
    return (
      <div>
        <div className={h3Row}>
          <h3 className={h3Txt}>{kind === 'ingreso' ? <TrendingUp size={13} className="text-emerald-600" /> : <TrendingDown size={13} className="text-red-500" />} {kind === 'ingreso' ? 'Ingresos' : 'Gastos'} ({ls.length}) · {money(total)}</h3>
          {isAdmin && <button onClick={() => { setNuevo(kind); setEdit(null); }} className="inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline"><Plus size={13} /> Agregar renglón</button>}
        </div>
        <div className="divide-y divide-line/60 overflow-hidden rounded-2xl border border-line bg-surface">
          {ls.map((l) => edit === l.id
            ? <FormRenglon key={l.id} inicial={l} onSave={(x) => guardar(x, l.id)} onCancel={() => setEdit(null)} />
            : (
              <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-content">{l.concept}{l.tipo === 'boletos' && <span className="ml-1.5 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold text-accent-ink">boletos</span>}</div>
                  {(l.qty != null || l.note) && <div className="text-xs text-content-3">{l.qty != null && l.unit_price != null ? `${l.qty} × ${money(l.unit_price)}` : ''}{l.qty != null && l.note ? ' · ' : ''}{l.note || ''}</div>}
                </div>
                <span className={`font-display text-sm font-semibold tabular-nums ${kind === 'ingreso' ? 'text-emerald-600' : 'text-content'}`}>{money(l.amount)}</span>
                {isAdmin && <button onClick={() => { setEdit(l.id); setNuevo(null); }} className="text-content-3 hover:text-content" title="Editar"><Pencil size={14} /></button>}
                {isAdmin && <button onClick={() => borrar(l)} className="text-content-3 hover:text-red-500" title="Borrar"><Trash2 size={14} /></button>}
              </div>
            ))}
          {nuevo === kind && <FormRenglon inicial={{ kind, tipo: kind === 'ingreso' && !ls.some((l) => l.tipo === 'boletos') ? 'boletos' : 'otro' }} onSave={(x) => guardar(x)} onCancel={() => setNuevo(null)} />}
          {ls.length === 0 && nuevo !== kind && <div className="px-4 py-3 text-sm text-content-3">Sin renglones.</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {isAdmin && otros.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-line bg-surface px-4 py-3 text-sm">
          <Copy size={15} className="text-content-3" /> <span className="text-content-2">Copiar los renglones de otro festival:</span>
          <select value={desde} onChange={(e) => setDesde(e.target.value)} className="rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-sm text-content">
            <option value="">Elige…</option>
            {otros.map((c) => <option key={c.event_id} value={c.event_id}>{c.nombre}</option>)}
          </select>
          <button onClick={copiar} disabled={!desde} className={btnSec}>Copiar</button>
        </div>
      )}
      {tabla('ingreso')}
      {tabla('gasto')}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-line bg-surface-2/60 px-4 py-3 text-sm">
        <span className="text-content-2">Ingresos {money(r.presupuesto.ingresos)} − gastos {money(r.presupuesto.gastos)}</span>
        <span className={`font-display text-lg font-bold ${r.presupuesto.utilidad >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>Utilidad {signo(r.presupuesto.utilidad)}</span>
      </div>
    </div>
  );
}

function FormRenglon({ inicial, onSave, onCancel }: { inicial: Partial<FestRenglon> & { kind: FestKind }; onSave: (l: Partial<FestRenglon> & { kind: FestKind }) => void; onCancel: () => void }) {
  const [concept, setConcept] = useState(inicial.concept || (inicial.tipo === 'boletos' ? 'Boletos' : ''));
  const [boletos, setBoletos] = useState(inicial.tipo === 'boletos');
  const [qty, setQty] = useState(inicial.qty != null ? String(inicial.qty) : '');
  const [unit, setUnit] = useState(inicial.unit_price != null ? String(inicial.unit_price) : '');
  const [amount, setAmount] = useState(inicial.amount != null ? String(inicial.amount) : '');
  const [note, setNote] = useState(inicial.note || '');
  const porCantidad = boletos || (qty !== '' && unit !== '');
  const calc = porCantidad && qty !== '' && unit !== '' ? Number(qty) * Number(unit) : null;
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ kind: inicial.kind, tipo: boletos ? 'boletos' : 'otro', concept, qty: qty === '' ? null : Number(qty), unit_price: unit === '' ? null : Number(unit), amount: calc ?? (amount === '' ? null : Number(amount)) as number, note: note || null }); }}
      className="space-y-2 bg-surface-2/50 px-4 py-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
        <input className={inputCls} placeholder="Concepto (ej. Renta del teatro)" value={concept} onChange={(e) => setConcept(e.target.value)} autoFocus />
        {inicial.kind === 'ingreso' && (
          <label className="inline-flex items-center gap-2 text-sm text-content-2"><input type="checkbox" checked={boletos} onChange={(e) => setBoletos(e.target.checked)} /> Es la venta de boletos</label>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <input className={inputCls} type="number" min="0" step="1" placeholder={boletos ? 'Boletos' : 'Cantidad (opcional)'} value={qty} onChange={(e) => setQty(e.target.value)} />
        <input className={inputCls} type="number" min="0" step="0.01" placeholder="Precio c/u" value={unit} onChange={(e) => setUnit(e.target.value)} />
        <input className={inputCls} type="number" min="0" step="0.01" placeholder="Monto" value={calc != null ? String(calc) : amount} disabled={calc != null} onChange={(e) => setAmount(e.target.value)} />
        <input className={inputCls} placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm text-content-2 hover:bg-surface-2"><X size={14} className="inline" /> Cancelar</button>
        <button type="submit" className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-on-accent"><Check size={14} className="inline" /> Guardar</button>
      </div>
    </form>
  );
}

// ── Movimientos (la libreta) ──────────────────────────────────────────────────
function Movimientos({ r, eventId, isAdmin, onChange, toast }: { r: FestResumen; eventId: number; isAdmin: boolean; onChange: () => void; toast: ReturnType<typeof useToast> }) {
  const libreta = r.evento.fin_mode === 'utilidad';
  const [form, setForm] = useState<null | { id?: number; m?: FestMov }>(null);
  const movs = useMemo(() => [...r.real.movimientos].sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : b.id - a.id)), [r]);
  const renglon = (id: number | null) => r.presupuesto.renglones.find((l) => l.id === id);

  async function borrar(m: FestMov) {
    if (!confirm(`¿Borrar «${m.concepto}» de ${money(m.amount)}?`)) return;
    try { if (m.origen === 'evento') await deleteMovimiento(eventId, m.id); else await deleteEventExpense(eventId, m.id); onChange(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo borrar'); }
  }

  return (
    <div className="space-y-4">
      <div className={h3Row}>
        <h3 className={h3Txt}><BookOpen size={13} className="text-accent-ink" /> {libreta ? 'Libreta del festival' : 'Movimientos'} ({movs.length})</h3>
        {isAdmin && <button onClick={() => setForm({})} className="inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline"><Plus size={13} /> {libreta ? 'Registrar ingreso o gasto' : 'Registrar gasto'}</button>}
      </div>
      <p className="-mt-2 text-xs text-content-3">
        {libreta ? 'Aquí van los boletos vendidos, los gastos y cualquier otro ingreso del festival. Si en Caja cobran un boleto como «Evento» con el nombre del festival, también aparece aquí (marcado «Caja»).'
          : 'Los boletos se cobran en Caja (tipo «Evento», con el nombre del evento) y aparecen aquí solos. Los gastos que registres aquí cuentan también en Finanzas › Gastos.'}
      </p>
      {form && isAdmin && <FormMov r={r} libreta={libreta} inicial={form.m} onCancel={() => setForm(null)} onSaved={() => { setForm(null); onChange(); }} eventId={eventId} toast={toast} />}
      {movs.length === 0 ? (
        <EmptyState icon={BookOpen} title="Sin movimientos todavía" subtitle={libreta ? 'Registra la primera venta de boletos o el primer gasto.' : 'Aún no hay cobros ni gastos de este evento.'} compact />
      ) : (
        <div className="divide-y divide-line/60 overflow-hidden rounded-2xl border border-line bg-surface">
          {movs.map((m) => (
            <div key={`${m.origen}-${m.id}`} className="flex items-center gap-3 px-4 py-2.5">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${m.kind === 'ingreso' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-500'}`}>
                {m.kind === 'ingreso' ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-content">{m.concepto}{m.qty ? <span className="ml-1 text-xs font-normal text-content-3">· {m.qty} boletos</span> : null}</div>
                <div className="truncate text-xs text-content-3">{fmtCorta(m.fecha)}{m.method ? ` · ${METODO[m.method] || m.method}` : ''}{renglon(m.budget_id) && renglon(m.budget_id)!.concept !== m.concepto ? ` · renglón «${renglon(m.budget_id)!.concept}»` : ''}{m.note ? ` · ${m.note}` : ''}</div>
              </div>
              {m.origen !== 'evento' && <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] text-content-3">{m.origen === 'caja' ? 'Caja' : 'Gastos'}</span>}
              <span className={`font-display text-sm font-semibold tabular-nums ${m.kind === 'ingreso' ? 'text-emerald-600' : 'text-red-500'}`}>{m.kind === 'ingreso' ? '+' : '−'}{money(m.amount)}</span>
              {isAdmin && m.origen === 'evento' && <button onClick={() => setForm({ m })} className="text-content-3 hover:text-content" title="Editar"><Pencil size={14} /></button>}
              {isAdmin && m.editable && <button onClick={() => borrar(m)} className="text-content-3 hover:text-red-500" title="Borrar"><Trash2 size={14} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FormMov({ r, libreta, inicial, eventId, onCancel, onSaved, toast }: { r: FestResumen; libreta: boolean; inicial?: FestMov; eventId: number; onCancel: () => void; onSaved: () => void; toast: ReturnType<typeof useToast> }) {
  const [kind, setKind] = useState<FestKind>(inicial?.kind || (libreta ? 'ingreso' : 'gasto'));
  const [budget, setBudget] = useState(inicial?.budget_id ? String(inicial.budget_id) : '');
  const [concept, setConcept] = useState(inicial?.concepto || '');
  const [fecha, setFecha] = useState(inicial?.fecha || todayISO());
  const [qty, setQty] = useState(inicial?.qty != null ? String(inicial.qty) : '');
  const [amount, setAmount] = useState(inicial ? String(inicial.amount) : '');
  const [method, setMethod] = useState(inicial?.method || 'cash');
  const [note, setNote] = useState(inicial?.note || '');
  const [saving, setSaving] = useState(false);
  const renglones = r.presupuesto.renglones.filter((l) => l.kind === kind);
  const sel = renglones.find((l) => String(l.id) === budget);
  const esBoletos = sel?.tipo === 'boletos';
  const precio = r.presupuesto.boletos.precio;

  function elegir(id: string) {
    setBudget(id);
    const l = renglones.find((x) => String(x.id) === id);
    if (l && (!concept || renglones.some((x) => x.concept === concept))) setConcept(l.concept);
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error('Monto inválido'); return; }
    if (!concept.trim()) { toast.error('Escribe el concepto'); return; }
    setSaving(true);
    try {
      if (libreta) {
        const body = { kind, concept: concept.trim(), budget_id: budget ? Number(budget) : null, fecha, qty: qty === '' ? null : Number(qty), amount: amt, method: kind === 'ingreso' ? method : null, note: note || null };
        if (inicial) await updateMovimiento(eventId, inicial.id, body); else await addMovimiento(eventId, body);
      } else {
        await addEventExpense(eventId, { amount: amt, category: concept.trim(), notes: note || undefined, date: fecha });
      }
      onSaved();
    } catch (err: any) { toast.error(err?.message || 'No se pudo guardar'); } finally { setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded-2xl border border-line bg-surface p-4 shadow-sm">
      {libreta && (
        <div className="flex gap-1">
          {(['ingreso', 'gasto'] as FestKind[]).map((k) => (
            <button type="button" key={k} onClick={() => { setKind(k); setBudget(''); }}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${kind === k ? (k === 'ingreso' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-red-500/15 text-red-600') : 'text-content-3 hover:bg-surface-2'}`}>
              {k === 'ingreso' ? 'Ingreso' : 'Gasto'}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {libreta && (
          <select className={inputCls} value={budget} onChange={(e) => elegir(e.target.value)}>
            <option value="">Renglón del presupuesto (opcional)</option>
            {renglones.map((l) => <option key={l.id} value={l.id}>{l.concept}</option>)}
          </select>
        )}
        <input className={inputCls} placeholder="Concepto" value={concept} onChange={(e) => setConcept(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <input className={inputCls} type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        {libreta && kind === 'ingreso' && (
          <input className={inputCls} type="number" min="0" step="1" placeholder="Boletos" value={qty}
            onChange={(e) => { setQty(e.target.value); if (esBoletos && precio && e.target.value !== '') setAmount(String(Number(e.target.value) * precio)); }} />
        )}
        <input className={inputCls} type="number" min="0" step="0.01" placeholder="Monto" value={amount} onChange={(e) => setAmount(e.target.value)} />
        {libreta && kind === 'ingreso' && (
          <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
            {Object.entries(METODO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        )}
      </div>
      <input className={inputCls} placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
        <button type="submit" disabled={saving} className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : 'Guardar'}</button>
      </div>
    </form>
  );
}

// ── Comparativo ───────────────────────────────────────────────────────────────
function Comparativo({ comp, actual }: { comp: FestComparativo | null; actual: number }) {
  if (!comp) return <Skeleton className="h-48 w-full rounded-2xl" />;
  if (comp.columnas.length === 0) return <EmptyState icon={Columns3} title="Nada que comparar" subtitle="Captura el presupuesto de al menos un evento." compact />;
  const cs = comp.columnas;
  const cel = (c: number) => `px-3 py-2 text-right tabular-nums ${c === actual ? 'bg-accent/10' : ''}`;
  const filaRes = (label: string, f: (x: typeof cs[number]) => string, bold = false) => (
    <tr className={`border-t border-line/60 ${bold ? 'font-semibold' : ''}`}>
      <td className="sticky left-0 z-10 bg-surface px-3 py-2 text-content">{label}</td>
      {cs.map((x) => <td key={x.event_id} className={`${cel(x.event_id)} text-content`}>{f(x)}</td>)}
    </tr>
  );
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-2xl border border-line bg-surface">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-content-3">
              <th className="sticky left-0 z-10 bg-surface px-3 py-2">Concepto</th>
              {cs.map((x) => (
                <th key={x.event_id} className={`px-3 py-2 text-right align-bottom ${x.event_id === actual ? 'bg-accent/10' : ''}`}>
                  <div className="normal-case text-sm font-bold tracking-normal text-content">{x.nombre}</div>
                  <div className="normal-case font-normal tracking-normal">{x.fecha ? fmtCorta(x.fecha) : ''} · {x.cifras}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filaRes('Boletos', (x) => (x.boletos != null ? String(x.boletos) : '—'))}
            {filaRes('Precio del boleto', (x) => (x.precio ? money(x.precio) : '—'))}
            {filaRes('Ingresos', (x) => money(x.ingresos), true)}
            {filaRes('Gastos', (x) => money(x.gastos), true)}
            <tr className="border-t border-line font-bold">
              <td className="sticky left-0 z-10 bg-surface px-3 py-2 text-content">Utilidad</td>
              {cs.map((x) => <td key={x.event_id} className={`${cel(x.event_id)} ${x.utilidad >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{signo(x.utilidad)}</td>)}
            </tr>
            {filaRes('Margen', (x) => (x.margen != null ? `${Math.round(x.margen)}%` : '—'))}
            {filaRes('Costo por boleto', (x) => (x.costoPorBoleto != null ? money(x.costoPorBoleto) : '—'))}
            {filaRes('Boletos para salir a mano', (x) => (x.equilibrio != null ? String(x.equilibrio) : '—'))}
            <tr className="border-t border-line"><td colSpan={cs.length + 1} className="sticky left-0 bg-surface px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-red-500">Gastos por concepto</td></tr>
            {comp.gastos.map((g) => (
              <tr key={g.concepto} className="border-t border-line/40">
                <td className="sticky left-0 z-10 bg-surface px-3 py-1.5 text-content-2">{g.concepto}</td>
                {cs.map((x) => <td key={x.event_id} className={`${cel(x.event_id)} py-1.5 text-content-2`}>{g.montos[x.event_id] != null ? money(g.montos[x.event_id]) : <span className="text-content-3">—</span>}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-content-3">Cada festival con las cifras de su presupuesto u hoja. Los gastos se juntan por nombre igual; nombres distintos (ej. «Renta del espacio» y «Renta del teatro») salen en renglones separados.</p>
    </div>
  );
}

// ── Ajustes del evento (modo, antecedente, concepto de la utilidad) ───────────
function Ajustes({ r, eventId, onDone }: { r: FestResumen; eventId: number; onDone: () => void }) {
  const toast = useToast();
  const [modo, setModo] = useState(r.evento.fin_mode);
  const [ref, setRef] = useState(r.evento.is_reference);
  const [concepto, setConcepto] = useState(r.evento.fin_concept_id ? String(r.evento.fin_concept_id) : '');
  const [conceptos, setConceptos] = useState<(FinConcept & { cat: string })[]>([]);
  useEffect(() => {
    getCatalog().then((c) => setConceptos(c.data.filter((s) => s.section === 'ingreso').flatMap((s) => s.categorias.flatMap((k) => k.conceptos.map((x) => ({ ...x, cat: k.name })))))).catch(() => {});
  }, []);
  async function guardar() {
    try { await setFestivalConfig(eventId, { fin_mode: modo, is_reference: ref, fin_concept_id: concepto ? Number(concepto) : null }); toast.success('Ajustes guardados'); onDone(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
  }
  return (
    <div className="space-y-3 rounded-2xl border border-line bg-surface p-4 shadow-sm">
      <h3 className={h3Cls}><Settings2 size={13} className="text-accent-ink" /> ¿Cómo entra este evento a Finanzas?</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {([['utilidad', 'Solo la utilidad', 'Ingresos y gastos se capturan aquí, en la libreta del evento; a Finanzas pasa un solo ingreso con la utilidad.'],
          ['completo', 'Completo', 'Los boletos se cobran en Caja y los gastos van a Finanzas › Gastos (como el Carnaval).']] as const).map(([k, t, d]) => (
          <label key={k} className={`cursor-pointer rounded-xl border p-3 text-sm ${modo === k ? 'border-accent bg-accent/10' : 'border-line'}`}>
            <input type="radio" name="modo" className="mr-2" checked={modo === k} onChange={() => setModo(k)} />
            <strong className="text-content">{t}</strong>
            <span className="mt-1 block text-xs text-content-3">{d}</span>
          </label>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-sm text-content-2">Concepto de ingreso para la utilidad
          <select className={`${inputCls} mt-1`} value={concepto} onChange={(e) => setConcepto(e.target.value)}>
            <option value="">— Elige —</option>
            {conceptos.map((c) => <option key={c.concept_id} value={c.concept_id}>{c.cat} › {c.name}</option>)}
          </select>
        </label>
        <label className="mt-6 inline-flex items-center gap-2 text-sm text-content-2"><input type="checkbox" checked={ref} onChange={(e) => setRef(e.target.checked)} /> Es un antecedente (festival pasado, solo para comparar)</label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onDone} className="rounded-lg px-3 py-1.5 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
        <button onClick={guardar} className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-on-accent">Guardar</button>
      </div>
    </div>
  );
}
